import React, { FC } from 'react';
import styled from '@emotion/styled';
import { colors } from '@modernhealth/vital-foundation';
import {
    BodyTextSmall,
    Badge,
    HeadingText30,
    BadgeProps,
    pxToRem,
} from '@modernhealth/vital-components';
import { TrackClick } from '@modernhealth/web-analytics';

import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { Avatar } from 'src/modules/messaging/components/Avatar';
import { ColorScheme } from 'src/generated/webapp_gql';

import { CareRecommendationBadge } from './CareRecommendedBadge';

export interface CareCardProps {
    title: string;
    titleTranslationKey: string;
    description: string;
    variant?: BadgeProps['variant'];
    images?: string[];
    label?: string;
    iconUrl?: string;
    onClick?: () => void;
    isRecommended?: boolean;
    initials?: string;
    backgroundImage?: string;
    colorScheme?: ColorScheme;
    index?: number;
}

interface CareCardImageProps {
    image: string;
    isDisabled: boolean;
    iconUrl?: string;
    isPlaceholder?: boolean;
    initials?: string;
}

const CareCardImage: FC<CareCardImageProps> = ({ image, isDisabled, iconUrl, initials }) => {
    if (!image && !initials && iconUrl) {
        return <IconAvatar src={iconUrl} alt="" />;
    }

    // For now, we assume that this means we're showing an Avatar image + an icon
    if ((image || initials) && iconUrl) {
        return (
            <ImageContainer>
                <ProviderAvatar
                    image={image}
                    initials={initials}
                    isDisabled={isDisabled}
                    diameter={90}
                />
                {iconUrl && (
                    <ImageIconContainer>
                        <ImageIcon src={iconUrl} alt="" />
                    </ImageIconContainer>
                )}
            </ImageContainer>
        );
    }

    // For now, we assume that this means we're showing a rectangular image with no icon
    // This is a temporary workaround to support "triplet" images referenced in https://joinmodernhealth.atlassian.net/browse/CAT-767
    if (image && !iconUrl) {
        return <Image src={image} isDisabled={isDisabled} />;
    }

    // This is likely a data misconfig if we get here - a Sentry error will be raised earlier
    // For now, just gracefully fail and display no image.
    return null;
};

export const CareCard: FC<CareCardProps> = ({
    title,
    titleTranslationKey,
    description,
    variant = 'default',
    images = [],
    label,
    iconUrl,
    onClick,
    isRecommended = false,
    initials,
    backgroundImage,
    colorScheme,
    index,
}) => {
    return (
        <TrackClick
            eventName="homepage: 1care: card"
            eventProperties={{
                element_index: index,
                recommended_badge: isRecommended,
                translation_key: titleTranslationKey,
            }}
        >
            <Container onClick={onClick}>
                <Column>
                    {isRecommended && <CareRecommendationBadge />}
                    <HeadingText30 marginTop={isRecommended ? 8 : 0} marginBottom={8}>
                        {title}
                    </HeadingText30>
                    <BodyTextSmall>{description}</BodyTextSmall>
                    {label && (
                        <BadgeContainer>
                            <Badge title={label} variant={variant} />
                        </BadgeContainer>
                    )}
                </Column>
                <Column css={{ marginLeft: pxToRem(30) }}>
                    <CareCardImage
                        image={images[0]}
                        isDisabled={variant === 'alert' || colorScheme === ColorScheme.Grayscale}
                        iconUrl={iconUrl}
                        initials={initials}
                    />
                </Column>
                {backgroundImage && <Background src={backgroundImage} />}
            </Container>
        </TrackClick>
    );
};

const Container = styled.button({
    width: '100%',
    maxWidth: pxToRem(668),
    minHeight: pxToRem(200),
    padding: pxToRem(32, 35),
    border: `solid 2px ${colors.gray05}`,
    borderRadius: pxToRem(8),
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'space-between',

    '&:focus-visible': {
        boxShadow: `0px 0px 0px 4px ${colors.cyan20}`,
    },

    '&:hover': {
        borderColor: colors.blue10,
        backgroundColor: hexToRgba(colors.blue05, 0.2),
    },
});

const Column = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
});

const BadgeContainer = styled.div({
    marginTop: pxToRem(20),
});

const AvatarImageWidthPx = 76;

const ProviderAvatar = styled(Avatar)<{ isDisabled?: boolean }>(({ isDisabled = false }) => ({
    filter: isDisabled ? 'grayscale(1)' : 'none',
    borderColor: colors.gray30,
    backgroundColor: colors.gray30,
}));

const IconAvatar = styled.img({
    width: pxToRem(AvatarImageWidthPx),
    height: pxToRem(AvatarImageWidthPx),
});

const ImageContainer = styled.div({
    position: 'relative',
});

const ImageIconContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    bottom: -10,
    right: -10,
});

const ImageIcon = styled.img({
    height: pxToRem(44),
    width: pxToRem(44),
});

const TripletImageWidthPx = 130;

const Image = styled.img<{ isDisabled?: boolean }>(({ isDisabled = false }) => ({
    width: pxToRem(TripletImageWidthPx),
    filter: isDisabled ? 'grayscale(1)' : 'none',
}));

const Background = styled.img({
    width: 'fit-content',
    position: 'absolute',
    right: 0,
    bottom: 0,
});
